import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DataFilters } from "../components/DataFilters";
import { downloadCsv } from "../domain/csv";
import {
  ageBandForGrade,
  gradeLabel,
  formatBirthdayLabel,
  matchesAgeFilter,
  schools,
  staff,
  type AgeFilter,
  type School,
  type Student,
} from "../domain/data";
import { mediaReleaseSummary, medicalSummary } from "../domain/permissionForm";
import { useForms, type FormAnswers, type FormSubmission, type SchoolYearForm } from "../state/forms";
import { useStudents } from "../state/students";
import { useAuth } from "../state/AuthProvider";
import { GuardianLoginSearch } from "../components/GuardianLoginLookup";
import { PrototypeBanner, StaffNav } from "../components/ui";

const GRID_HEADERS = [
  "Student",
  "Guardian phone",
  "Birthday",
  "Email",
  "School district",
  "Grade",
  "Age",
  "MS/HS",
  "Guardian",
  "Media approval",
];

const EXPORT_HEADERS = [
  "Student",
  "Medical",
  "Media release",
  "Birthday",
  "Email",
  "School district",
  "Grade",
  "Age",
  "MS/HS",
  "Guardian",
  "Guardian email",
  "Guardian phone",
  "Emergency",
  "Emergency phone",
];

function currentAnswers(
  studentId: string,
  forms: SchoolYearForm[],
  submissions: FormSubmission[],
): FormAnswers | undefined {
  const history = submissions.flatMap((row) => {
    if (row.studentId !== studentId) return [];
    const form = forms.find((item) => item.id === row.formId);
    return form ? [{ row, form }] : [];
  });
  history.sort((a, b) => (a.form.year < b.form.year ? 1 : -1));
  const current = history.find((item) => item.form.status === "open") ?? history[0];
  return current?.row.answers;
}

function fieldValues(student: Student, answers: FormAnswers | undefined): Array<string | number> {
  return [
    `${student.given} ${student.family}`,
    medicalSummary(answers?.permission, answers?.medical),
    mediaReleaseSummary(answers?.permission),
    formatBirthdayLabel(student.birthday),
    student.email,
    student.school,
    gradeLabel(student.grade),
    student.age,
    ageBandForGrade(student.grade),
    student.guardianName,
    student.guardianEmail,
    student.guardianPhone,
    student.emergencyName,
    student.emergencyPhone,
  ];
}

function matchesStudentSearch(student: Student, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    student.given,
    student.family,
    `${student.given} ${student.family}`,
    student.email,
    student.guardianName,
    student.guardianEmail,
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
}

export function StaffRoster() {
  const { students } = useStudents();
  const { forms, submissions } = useForms();
  const { account } = useAuth();
  const [age, setAge] = useState<AgeFilter>("all");
  const [school, setSchool] = useState<School | "all">("all");
  const [query, setQuery] = useState("");

  const staffDisplayName = account
    ? `${account.given} ${account.family}`
    : `${staff.given} ${staff.family}`;

  const rows = useMemo(
    () =>
      students.filter((student) => {
        if (!matchesAgeFilter(student.grade, age)) return false;
        if (school !== "all" && student.school !== school) return false;
        if (!matchesStudentSearch(student, query)) return false;
        return true;
      }),
    [students, age, school, query],
  );

  const visibleSchools = school === "all" ? schools : [school];
  const elemCount = rows.filter((student) => ageBandForGrade(student.grade) === "ELEM").length;
  const msCount = rows.filter((student) => ageBandForGrade(student.grade) === "MS").length;
  const hsCount = rows.filter((student) => ageBandForGrade(student.grade) === "HS").length;

  function exportRoster() {
    downloadCsv(
      "lift-roster.csv",
      EXPORT_HEADERS,
      rows.map((student) =>
        fieldValues(student, currentAnswers(student.id, forms, submissions)),
      ),
    );
  }

  function clearFilters() {
    setAge("all");
    setSchool("all");
    setQuery("");
  }

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell shell--wide">
        <StaffNav />
        <p className="meta">
          Staff · {staffDisplayName} · 2026-2027
        </p>
        <h1>Schools & students</h1>
        <p className="lede">
          Filter by name, email, age, or district. Guardian phone and media
          approval are on this list. Open a student for medical notes and the
          rest of the form. Look up a guardian email when a family calls about
          a login they do not remember.
        </p>

        <GuardianLoginSearch />

        <div className="field roster-search">
          <label htmlFor="roster-search">Search</label>
          <input
            id="roster-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Name, email, or guardian"
          />
        </div>

        <DataFilters
          age={age}
          onAge={setAge}
          school={school}
          onSchool={setSchool}
          onClear={clearFilters}
        />

        <div className="actions actions-split" style={{ marginTop: 0, marginBottom: "1rem" }}>
          <div className="actions-group">
            <Link className="btn" to="/staff/students/new">
              New student
            </Link>
            <button type="button" onClick={exportRoster} disabled={rows.length === 0}>
              Export CSV
            </button>
            <Link className="btn" to="/staff/students/upload">
              Import Students
            </Link>
          </div>
          <div className="actions-group">
            <Link className="btn" to="/staff/forms">
              School-year forms
            </Link>
            <Link className="btn" to="/staff/events">
              Events & attendance
            </Link>
            <Link className="btn ghost" to="/staff/events/new">
              Create event
            </Link>
          </div>
        </div>

        <div className="wire" style={{ marginBottom: "1.25rem" }}>
          <div className="meta">Showing</div>
          <strong style={{ fontSize: "1.5rem" }}>
            {rows.length} of {students.length}
          </strong>
          <div className="anno">
            Elem {elemCount} · MS {msCount} · HS {hsCount}
          </div>
        </div>

        {visibleSchools.map((district) => {
          const list = rows.filter((student) => student.school === district);
          if (list.length === 0) return null;
          return (
            <section key={district}>
              <h2>
                {district} <span className="tag muted">{list.length}</span>
              </h2>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      {GRID_HEADERS.map((header) => (
                        <th key={header}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((s) => {
                      const answers = currentAnswers(s.id, forms, submissions);
                      return (
                        <tr key={s.id}>
                          <td>
                            <Link to={`/student/${s.id}/profile`}>
                              {s.given} {s.family}
                            </Link>
                          </td>
                          <td>{s.guardianPhone}</td>
                          <td>{formatBirthdayLabel(s.birthday)}</td>
                          <td>{s.email}</td>
                          <td>{s.school}</td>
                          <td>{gradeLabel(s.grade)}</td>
                          <td>{s.age}</td>
                          <td>{ageBandForGrade(s.grade)}</td>
                          <td>{s.guardianName}</td>
                          <td>{mediaReleaseSummary(answers?.permission)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })}

        {rows.length === 0 ? (
          <div className="wire dashed">
            <div className="meta">No students match these filters</div>
          </div>
        ) : null}
      </main>
    </div>
  );
}
